import { create } from 'apisauce';
import dayjs from 'dayjs';
import store from 'containers/store';
import * as actions from 'containers/App/actions';
import { getAuthToken } from 'utils/cookies';
import { api as apApi } from './index';

const token = getAuthToken();

const getDefaultLead = (orgId) => {
  return {
    'contact_first_name': '',
    'contact_last_name': '',
    'contact': {
      'phones': [
        {
          'field_value': '',
        },
      ],
      'emails': [
        {
          'field_value': '',
        },
      ],
      'addresses': [
        {
          '_isCreating': true,
          'city': '',
          'country': '',
        },
      ],
    },
    'status': 'new',
    'estimated_budget': null,
    'estimated_guest_count': null,
    'address': {
      'city': '',
      'region': '',
    },
    'timezone': null,
    'party': [
      [
        'bride',
        '',
        '',
      ],
      [
        'groom',
        '',
        '',
      ],
    ],
    'wedding_date': null,
    'date_inquiry': dayjs().format(),
    'organization_id': orgId,
    'notes': null,
  };
};

const newLead = (orgId) => {
  return new Promise((resolve, reject) => {
    const input = getDefaultLead(orgId);

    const api = create({
      baseURL: `${process.env.REACT_APP_AP_URL}`,
      headers: {
        'content-type': 'application/json, text/plain, */*',
        'X-Requested-With': 'XMLHttpRequest',
        'Authorization': `Bearer ${token}`,
      },
    });

    try {
      api.post(`/api/organization/${orgId}/leads`, input)
        .then(response => {
          // Redirect to login if auth denied.
          if (response.status === 401) {
            if (store) {
              store.dispatch(actions.pushAuthError(response.data?.signin_url ?? '/signin' ));
              return;
            }
          }

          resolve(response);
        })
        .catch(error => {
          return reject(error);
        });
    }
    catch(error) {
      reject(error);
    }
  });
};

const createLead = (orgId, data) => {
  return apApi.post(`/api/organization/${orgId}/leads`, data);
};


const leadComment = (orgId, leadId) => {
  return new Promise((resolve, reject) => {
    const input = {
      'body': 'Created lead',
      'kind': 'aisleplanner',
    };
    const api = create({
      baseURL: `${process.env.REACT_APP_AP_URL}`,
      headers: {
        'content-type': 'application/json, text/plain, */*',
        'X-Requested-With': 'XMLHttpRequest',
        'Authorization': `Bearer ${token}`,
      },
    });

    try {
      api.post(`/api/organization/${orgId}/leads/${leadId}/comments`, input)
          .then(response => {
            // Redirect to login if auth denied.
            if (response.status === 401) {
              if (store) {
                store.dispatch(actions.pushAuthError(response.data?.signin_url ?? '/signin' ));
              }
            }

            resolve(response);
          })
          .catch(error => {
            return reject(error);
          });
    }
    catch(error) {
      reject(error);
    }

  });
};

const streamDownload = ({ orgId, params }) => {
  return new Promise((resolve, reject) => {
    const input = {
      client_offset: (new Date().getTimezoneOffset()),
      download_type: 'org.leads-csv',
      params,
    };
    const api = create({
      baseURL: `${process.env.REACT_APP_AP_URL}`,
      headers: {
        'content-type': 'application/json, text/plain, */*',
        'X-Requested-With': 'XMLHttpRequest',
        'Authorization': `Bearer ${token}`,
      },
    });

    try {
      api.post(`/api/organization/${orgId}/leads/downloads`, input)
        .then(response => {
          // Redirect to login if auth denied.
          if (response.status === 401) {
            if (store) {
              store.dispatch(actions.pushAuthError(response.data?.signin_url ?? '/signin' ));
              resolve(response);
            }
          }
          poll(response, api, input, orgId)
            .then(downloadResponse => {
              resolve(downloadResponse);
            })
            .catch(error => {
              return reject(error);
            });
        });
    }
    catch (error) {
      return Promise.reject(error);
    };
  });
};

const poll = (response, api, input, orgId) => {
  return new Promise((resolve, reject) => {
    if (response.data.status === 'ready') {
      resolve(response);
    }
    else if (response.data.status === 'error') {
      reject(new Error('Promise failed'));
    }
    else {
      setTimeout(() => {
        try {
          api.get(`/api/organization/${orgId}/leads/downloads/${response.data.token}`, input)
            .then((getResponse) => {
              resolve(poll(getResponse, api, input, orgId)
                .catch(error => {
                  return Promise.reject(error);
                }),
              );
            });
        }
        catch (error) {
          reject(error);
        }
      }, 3000);
    }
  });
};

const getLead = (orgId, leadId) => {
  return apApi.get(`/api/organization/${orgId}/leads/${leadId}`, {}, { headers: { 'API-Version': '2.0' } });
};

const updateLead = (orgId, leadId, input) => {
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}`, input);
};

const updateLeadContactPhone = (orgId, leadId, phoneId, input) => {
  if (!phoneId) {
    return apApi.post(
      `/api/organization/${orgId}/leads/${leadId}/contact/phones`,
      { ...input, _isCreating: true },
    );
  }
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}/contact/phones/${phoneId}`, input);
};

const updateLeadContactEmail = (orgId, leadId, emailId, input) => {
  if (!emailId) {
    return apApi.post(
      `/api/organization/${orgId}/leads/${leadId}/contact/emails`,
      { ...input, _isCreating: true },
    );
  }
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}/contact/emails/${emailId}`, input);
};

const updateLeadContactAddress = (orgId, leadId, addressId, input) => {
  if (!addressId) {
    return apApi.post(
      `/api/organization/${orgId}/leads/${leadId}/contact/addresses`,
      { ...input, _isCreating: true },
    );
  }
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}/contact/addresses/${addressId}`, input);
};

const updateLeadContactWebsite = (orgId, leadId, websiteId, input) => {
  if (!websiteId) {
    return apApi.post(
      `/api/organization/${orgId}/leads/${leadId}/contact/websites`,
      { ...input, _isCreating: true },
    );
  }
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}/contact/websites/${websiteId}`, input);
};

const updateLeadPrimaryContact = (orgId, leadId, input) => {
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}/contact`, input);
};

const fetchLeadAssignments = (orgId, leadId) => {
  return apApi.get(`/api/organization/${orgId}/leads/${leadId}/lead-assignments`);
};

const fetchChecklistItems = (orgId, leadId) => {
  return apApi.get(`/api/organization/${orgId}/leads/${leadId}/checklist`);
};

const createChecklistItem = (orgId, leadId, referenceId = null) => {
  return apApi.post(`/api/organization/${orgId}/leads/${leadId}/checklist/items`, { reference: referenceId });
};

const updateChecklistItem = (orgId, leadId, checklistItemId, input) => {
  return apApi.put(`/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}`, input);
};

const deleteChecklistItem = (orgId, leadId, checklistItemId) => {
  return apApi.delete(`/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}`);
};

const reorderChecklistItem = (orgId, leadId, checklistItemId, overTaskId) => {
  return apApi.post(`/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}?order_before`, {
    reference: overTaskId,
  });
};

const addChecklistAssignment = (orgId, leadId, loginId, checklistItemId) => {
  return apApi.post(`/api/organization/${orgId}/leads/${leadId}/checklist/logins/${loginId}/items`,
    {
      login_id: loginId,
      focus_level: 2,
      task_id: checklistItemId,
      assignment_reference: null,
    });
};

const removeChecklistAssignment = (orgId, leadId, loginId, checklistItemId) => {
  return apApi.post(
    `/api/organization/${orgId}/leads/${leadId}/checklist/logins/${loginId}/items/${checklistItemId}?drop`,
  );
};

const getChecklistComments = (orgId, leadId, checklistItemId) => {
  return apApi.get(`/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}/comments`);
};

const addChecklistComment = (orgId, leadId, checklistItemId, comment) => {
  return apApi.post(`/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}/comments`, {
      body: comment,
    });
};

const editChecklistComment = (orgId, leadId, checklistItemId, commentId, comment) => {
  return apApi.put(
    `/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}/comments/comment/${commentId}`, {
      body: comment,
    });
};

const deleteChecklistComment = (orgId, leadId, checklistItemId, commentId) => {
  return apApi.delete(
    `/api/organization/${orgId}/leads/${leadId}/checklist/items/${checklistItemId}/comments/comment/${commentId}`,
  );
};

const fetchChecklistReminders = (orgId, leadId, taskId) => {
  return apApi.get(
    `/api/organization/${orgId}/leads/${leadId}/checklist/items/${taskId}/reminders`,
  );
};

const createChecklistReminder = (orgId, leadId, taskId, input) => {
  return apApi.post(
    `/api/organization/${orgId}/leads/${leadId}/checklist/items/${taskId}/reminders`, input,
  );
};

const updateChecklistReminder = (orgId, leadId, taskId, reminderId, input) => {
  return apApi.put(
    `/api/organization/${orgId}/leads/${leadId}/checklist/items/${taskId}/reminders/${reminderId}`, input,
  );
};

const deleteChecklistReminder = (orgId, leadId, taskId, reminderId) => {
  return apApi.delete(
    `/api/organization/${orgId}/leads/${leadId}/checklist/items/${taskId}/reminders/${reminderId}`,
  );
};

const fetchLeadChecklistTemplates = (orgId) => {
  return apApi.get(`/api/organization/${orgId}/templates/lead-checklists`);
};

const createLeadChecklistFromTemplate = (orgId, leadId, templateId) => {
  return apApi.post(
    `/api/organization/${orgId}/leads/${leadId}/checklist/add-from-template`,
    { template_id: templateId },
  );
};

const duplicateLead = (orgId, leadId) => {
  return apApi.post(`/api/organization/${orgId}/leads/${leadId}/copy`);
};

const deleteQuote = (orgId, quoteId) => {
  return apApi.delete(`/api/organization/${orgId}/quotes/${quoteId}`);
};

const deleteInvoice = (orgId, invoiceId) => {
  return apApi.delete(`/api/organization/${orgId}/invoices/${invoiceId}`);
};

export { newLead };
export { createLead };
export { leadComment };
export { streamDownload };
export { getLead };
export { updateLead };
export { updateLeadContactPhone };
export { updateLeadContactEmail };
export { updateLeadContactAddress };
export { updateLeadContactWebsite };
export { fetchLeadAssignments };
export { fetchChecklistItems };
export { createChecklistItem };
export { updateChecklistItem };
export { deleteChecklistItem };
export { reorderChecklistItem };
export { addChecklistAssignment };
export { removeChecklistAssignment };
export { getChecklistComments };
export { addChecklistComment };
export { editChecklistComment };
export { deleteChecklistComment };
export { fetchChecklistReminders };
export { createChecklistReminder };
export { updateChecklistReminder };
export { deleteChecklistReminder };
export { fetchLeadChecklistTemplates };
export { createLeadChecklistFromTemplate };
export { duplicateLead };
export { deleteQuote };
export { deleteInvoice };
export { updateLeadPrimaryContact };
